import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, EyeOffIcon, LockIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { StandingChip } from '../../components/verification/StandingChip';
import { useAccount } from '../../contexts/AccountContext';
import { standingFor } from '../../data/standings';
import { LEVEL_LABELS } from './Settings';
import type { VisibilityLevel, VisibilitySurface } from '../../types/identity';

/** Audiences a person can put themselves in the position of. */
const AUDIENCES = ['Anyone on Campuzon', 'Someone at my institution', 'Someone I have accepted'] as const;
type Audience = (typeof AUDIENCES)[number];

/**
 * Which stored levels are satisfied by each audience. A stranger sees only what
 * is public; an accepted connection sees the most. This mirrors the rule the
 * profile service applies — the preview is not a separate, kinder judgement.
 */
const REACH: Record<Audience, VisibilityLevel[]> = {
  'Anyone on Campuzon': ['public'],
  'Someone at my institution': ['public', 'campus'],
  'Someone I have accepted': ['public', 'campus', 'connections']
};

/**
 * Your profile from the outside.
 *
 * A privacy setting you cannot see the effect of is not really a control, so
 * this renders the saved settings from someone else's position — and names every
 * field it withholds, because a silently absent field teaches you nothing.
 */
export function PublicProfilePreview() {
  const {
    account,
    institutionName,
    campusName
  } = useAccount();
  const [audience, setAudience] = useState<Audience>(AUDIENCES[0]);
  const standing = standingFor(account.username);
  const reach = REACH[audience];
  const rows: {
    surface: VisibilitySurface;
    label: string;
    value: string;
  }[] = [{
    surface: 'academic',
    label: 'Academic profile',
    value: `${account.academic.programme} · ${account.academic.level}`
  }, {
    surface: 'housing',
    label: 'Housing activity',
    value: 'Applied to 2 residences this term'
  }, {
    surface: 'roommate',
    label: 'Roommate profile',
    value: 'Quiet evenings · non-smoking · shares cooking'
  }, {
    surface: 'career',
    label: 'Career profile',
    value: 'Systems modelling, Python, CAD'
  }, {
    surface: 'contact',
    label: 'Contact details',
    value: `${account.email} · +234 ••• ••• ••••`
  }];
  const visible = rows.filter(row => reach.includes(account.visibility[row.surface]));
  const withheld = rows.filter(row => !reach.includes(account.visibility[row.surface]));
  return <div className="space-y-6" data-id="element-10941" data-mp="wcxbl1wlkeon">
      <Link to="/settings" className="inline-flex items-center gap-1.5 text-body-sm font-medium text-ink-muted hover:text-ink" data-id="element-10942" data-mp="3m2ipczr551q1">
        <ArrowLeftIcon aria-hidden className="h-4 w-4" data-id="element-10943" data-mp="3h1urbu4hvkmb" />
        Settings
      </Link>

      <PageHeader eyebrow="Preview" title="Your profile, as others see it" description="Rendered from your saved privacy settings. Change the audience to see what each one reaches." data-id="element-10944" data-mp="1wmgae21vr5ev" />

      <Tabs tabs={AUDIENCES} value={audience} onChange={setAudience} label="Audience" data-id="element-10945" data-mp="25kigux4duq9j" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-10946" data-mp="2adla1458zxp7">
        <Panel title={`What ${audience.toLowerCase()} sees`} bodyClassName="p-0" data-id="element-10947" data-mp="2fceey86gygdr">
          <div className="border-b border-line px-5 py-5 sm:px-6" data-id="element-10948" data-mp="2kapo0goxwmux">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-id="element-10949" data-mp="2kd2fbkgl89kn">
              <h2 className="text-h4 font-semibold text-ink" data-id="element-10950" data-mp="2pbdodsz26g1t">{account.fullName}</h2>
              <StandingChip standing={standing} data-id="element-10951" data-mp="2tamhqwq6sbwu" />
            </div>
            <p className="cz-num mt-1 text-body-sm text-ink-muted" data-id="element-10952" data-mp="uw1f4pfk5n1n">@{account.username}</p>
            <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10953" data-mp="yxmwamz4knhn">
              {institutionName} · {campusName}
            </p>
            <p className="mt-2 max-w-[70ch] text-caption text-ink-subtle" data-id="element-10954" data-mp="3mx2vk29h1a6">
              Your name, handle and institution are always visible — they are how people know who they
              are dealing with. Your internal Campuzon id and financial identifier never appear here.
            </p>
          </div>

          {visible.length === 0 ? <p className="px-5 py-5 text-body-sm text-ink sm:px-6" data-id="element-10955" data-mp="8ncdw4z2lwoz">
              Nothing beyond your name and handle reaches this audience. That is a valid choice, but it
              also means a landlord or a potential roommate has very little to go on.
            </p> : <ul className="divide-y divide-line" data-id="element-10956" data-mp="3qtqrg2tfg4mu">
              {visible.map(row => <li key={row.surface} className="px-5 py-4 sm:px-6" data-id="element-10957" data-mp="3m0nz1y0dz188">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1" data-id="element-10958" data-mp="2yb3c74143jto">
                    <p className="text-body-sm text-ink-muted" data-id="element-10959" data-mp="2tda4g6k5uhra">{row.label}</p>
                    <Badge plain data-id="element-10960" data-mp="18upyt0ctx2on">{LEVEL_LABELS[account.visibility[row.surface]]}</Badge>
                  </div>
                  <p className="mt-1 text-body text-ink" data-id="element-10961" data-mp="1dl6e6q07mydn">{row.value}</p>
                </li>)}
            </ul>}

          {withheld.length > 0 && <div className="border-t border-line bg-canvas px-5 py-4 sm:px-6" data-id="element-10962" data-mp="zs8bsjpco60l">
              <p className="inline-flex items-center gap-2 text-body font-semibold text-ink" data-id="element-10963" data-mp="13vwhewuf8rj7">
                <EyeOffIcon aria-hidden className="h-4 w-4 text-ink-subtle" data-id="element-10964" data-mp="1rm0lf5biutaq" />
                Withheld from this audience
              </p>
              <ul className="mt-2 space-y-1.5" data-id="element-10965" data-mp="1ikszznu3tmy2">
                {withheld.map(row => <li key={row.surface} className="text-body-sm text-ink-muted" data-id="element-10966" data-mp="1njm1ehw49nhv">
                    {row.label} — set to {LEVEL_LABELS[account.visibility[row.surface]].toLowerCase()}
                  </li>)}
              </ul>
              <p className="mt-2 max-w-[70ch] text-caption text-ink-subtle" data-id="element-10967" data-mp="c5idntg4gvl9">
                Others are told a field is withheld, not that it does not exist. Hiding something never
                implies you have nothing to show.
              </p>
            </div>}
        </Panel>

        <div className="space-y-5" data-id="element-10968" data-mp="242rvk1nw60wp">
          <Panel title="Never visible to anyone" data-id="element-10969" data-mp="241qixsr896cv">
            <ul className="space-y-2" data-id="element-10970" data-mp="20319ucd82az1">
              {['Private notes and diary entries', 'Results, transcripts and attendance', 'Wellbeing and support activity', 'Balances, ledger entries and your financial identifier', 'Your student identifier and institutional email', 'Your home or residence address'].map(item => <li key={item} className="flex items-start gap-2 text-body-sm text-ink" data-id="element-10971" data-mp="1h56r36makl3g">
                  <LockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-10972" data-mp="3figiwu5251y1" />
                  {item}
                </li>)}
            </ul>
            <p className="mt-3 max-w-[58ch] border-t border-line pt-3 text-caption text-ink-subtle" data-id="element-10973" data-mp="xaf7aqp4ousr">
              These are not settings you can loosen. No audience, and no operator console, reaches them.
            </p>
          </Panel>

          <Panel title="Change something" data-id="element-10974" data-mp="1708i3phg55vy">
            <p className="max-w-[58ch] text-body-sm text-ink" data-id="element-10975" data-mp="1b1k0pb1wkqun">
              Adjust a setting and come back — the preview reads whatever you have saved.
            </p>
            <Link to="/settings" className="mt-3 inline-block" data-id="element-10976" data-mp="ed4adcqyydgh">
              <Button size="sm" variant="secondary" data-id="element-10977" data-mp="jano8bjr8o7w">
                Privacy settings
              </Button>
            </Link>
          </Panel>
        </div>
      </div>
    </div>;
}